import React, { useState, useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useHistory } from 'react-router-dom';
import { URL } from '../core/config';
import { useSemester } from '../core/SemesterContext';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentSemester } from '../core/store/actions/semester';
import { hasPermission } from '../core/utils/permissionUtils';
import '../styles/semester-selector.css';
import axios from 'axios';

const semestersURL = URL + "/semesters";

const SemesterSelector = () => {
    const [semesters, setSemesters] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const { currentSemester } = useSemester();
    const dispatch = useDispatch();
    const history = useHistory();
    const userProfile = useSelector((state) => state.user.profile);
    const canManageSemesters = hasPermission(userProfile, 'MANAGE_SEMESTERS');

    // Track if component is mounted
    const isMountedRef = useRef(true);

    useEffect(() => {
        const fetchSemesters = async () => {
            try {
                const response = await axios.get(`${semestersURL}/list`);
                const data = response.data;

                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    setSemesters(data);
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error fetching semesters:", error);
                // Only update state if component is still mounted
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        };

        fetchSemesters();

        // Cleanup function
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (isMountedRef.current && !e.target.closest('.semester-selector-container')) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSemesterSelect = (semester) => {
        // Only dispatch if component is still mounted
        if (isMountedRef.current) {
            dispatch(setCurrentSemester(semester));
            setIsOpen(false);
        }
    };

    const toggleDropdown = () => {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
            setIsOpen(!isOpen);
        }
    };

    if (loading) {
        return (
            <div className="header-buttons">
                <Icon icon="eos-icons:loading" className="header-button-icon" />
                <p>Loading...</p>
            </div>
        );
    }

    return (
      <div className="semester-selector-container">
        <button
          className={`header-buttons semester-button ${currentSemester && !currentSemester.isActive ? 'past-semester' : ''}`}
          onClick={toggleDropdown}
        >
          <Icon
          icon={currentSemester && !currentSemester.isActive ? "material-symbols:history" : "material-symbols:calendar-clock-outline"}
          className="header-button-icon"
          />
          <p>{currentSemester ? currentSemester.name : 'Select Semester'}</p>
          <Icon
          icon={isOpen ? "material-symbols:expand-less" : "material-symbols:expand-more"}
          className="dropdown-arrow"
          />
        </button>

            {isOpen && (
                <div className="semester-dropdown">
                    {semesters.map((semester) => (
                        <button
                            key={semester._id}
                            className={`semester-option ${currentSemester?._id === semester._id ? 'active' : ''}`}
                            onClick={() => handleSemesterSelect(semester)}
                        >
                            <div className="semester-info">
                                <span className="semester-name">{semester.name}</span>
                                {semester.isActive && (
                                    <span className="active-badge">Current</span>
                                )}
                            </div>
                        </button>
                    ))}

                    {canManageSemesters && (
                        <button
                            className="semester-option manage-semesters"
                            onClick={() => {
                                setIsOpen(false);
                                history.push('/semester-management');
                            }}
                        >
                            <span className="semester-name">Manage Semesters</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default SemesterSelector;
